<template>
  <div class="col-12 col-xl-12" v-can="'groups.display'">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ t('groups.classGroups') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="t('groups.loading')"
            class="py-5"
          />

          <template v-else>
            <div class="d-block d-xl-none">
              <div v-if="classGroups.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-users-slash fa-2x mb-2"></i>
                <p>{{ t('groups.noGroups') }}</p>
              </div>

              <div 
                v-for="group in classGroups" 
                :key="'mobile-' + group.id" 
                class="card mb-3 mx-2"
                @click="navigateToStudents(group)"
                style="cursor: pointer;"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-0 text-sm">{{ group.group?.name || group.name }}</h6>
                      <p class="text-xs text-muted mb-0">{{ t('groups.createdAt') }}: {{ formatDate(group.created_at) }}</p>
                    </div>
                  </div>
                  
                  <div class="d-flex justify-content-end mt-2" @click.stop>
                    <router-link 
                      v-if="!classId"
                      :to="`/groups/edit/${group.id}`"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="t('groups.edit')"
                      v-can="'groups.update'"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </router-link>
                    <button 
                      v-else
                      @click="handleEdit(group)"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="t('groups.edit')"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </button>
                    <button 
                      @click="openDeleteDialog(group)"
                      class="btn btn-link text-danger p-0 mx-1"
                      :title="t('groups.delete')"
                      v-can="'groups.delete'"
                    >
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('groups.groupName') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('groups.createdAt') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ t('groups.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="classGroups.length === 0">
                  <td colspan="4" class="text-center py-4 text-muted">
                    <i class="fas fa-users-slash fa-2x mb-2"></i>
                    <p>{{ t('groups.noGroups') }}</p>
                  </td>
                </tr>
                
                <tr 
                  v-for="group in classGroups" 
                  :key="group.id" 
                  @click="navigateToStudents(group)"
                  style="cursor: pointer;"
                >
                  <td>
                    <div class="d-flex px-3 py-1">
                      <div class="d-flex flex-column justify-content-center">
                        <h6 class="mb-0 text-md">{{ group.group?.name || group.name }}</h6>
                      </div>
                    </div>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">{{ formatDate(group.created_at) }}</p>
                  </td>

                  <td class="align-middle text-center pe-4" @click.stop>
                    <div class="d-inline-flex">
                      <router-link 
                        v-if="!classId"
                        :to="`/groups/edit/${group.id}`"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="t('groups.edit')"
                        v-can="'groups.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </router-link>
                      <button 
                        v-else
                        @click="handleEdit(group)"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="t('groups.edit')"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </button>
                      <button 
                        @click="openDeleteDialog(group)"
                        class="btn btn-link text-danger p-1 mx-1"
                        :title="t('groups.delete')"
                        v-can="'groups.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
    
    <DeleteGroupDialog
      ref="deleteDialog"
      :group-name="selectedGroup?.group?.name || selectedGroup?.name"
      @delete="handleDeleteGroup"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import DeleteGroupDialog from "@/components/features/groups/dialogs/DeleteGroupDialog.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t } = useI18n();

const deleteDialog = ref(null);
const selectedGroup = ref(null);
const router = useRouter();
const emit = defineEmits(['delete-group']);
const props = defineProps({
  classGroups: { type: Array, required: true },
  className: { type: String, required: true },
  classId: { type: [String, Number], default: null },
  loading: { type: Boolean, default: false }
});

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('ar', options);
};

const navigateToStudents = (group) => {
  if (props.classId) {
    router.push({
      path: `/classes/${props.classId}/students`,
      query: { group: group.id }
    });
  }
};

const openDeleteDialog = (group) => {
  selectedGroup.value = group;
  deleteDialog.value.openDialog();
};

const handleDeleteGroup = () => {
  emit('delete-group', selectedGroup.value.id); 
  deleteDialog.value.closeDialog();
};
</script>
